/**
 * First-touch attribution for durable lead capture (2026-07-17 leads schema
 * review). UTM params exist only on the landing URL — by the time a visitor
 * reaches /get-started via internal nav they're gone — so main.tsx records
 * first-touch once per tab (sessionStorage) before the router mounts, and the
 * lead form reads it at submit.
 *
 * Client-side mirror of the server allowlist (backend leads.utils): utm_*
 * keys, landing_path (path only, no query), referrer_origin (origin only,
 * external referrers only). Never ad-click IDs (gclid/fbclid) or full URLs.
 * The server re-sanitises regardless — this is minimisation at the earliest
 * point, not the enforcement layer.
 */

export const FIRST_TOUCH_KEY = 'nm_first_touch';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type FirstTouchAttribution = Partial<
  Record<(typeof UTM_KEYS)[number] | 'landing_path' | 'referrer_origin', string>
>;

/**
 * Record first-touch once per tab. No-op if already recorded (first touch
 * wins) or if sessionStorage is unavailable (private modes) — attribution is
 * best-effort and must never break the site.
 */
export function recordFirstTouch(): void {
  try {
    if (sessionStorage.getItem(FIRST_TOUCH_KEY) !== null) return;

    const record: FirstTouchAttribution = {
      landing_path: window.location.pathname,
    };

    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) record[key] = value;
    }

    if (document.referrer) {
      try {
        const origin = new URL(document.referrer).origin;
        // External referrers only — same-site nav is not attribution.
        if (origin !== window.location.origin) {
          record.referrer_origin = origin;
        }
      } catch {
        // unparseable referrer — skip
      }
    }

    sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage unavailable — attribution silently absent
  }
}

/** The recorded first-touch shape, or null if absent/corrupt/unavailable. */
export function getFirstTouchAttribution(): FirstTouchAttribution | null {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
      return null;
    return parsed as FirstTouchAttribution;
  } catch {
    return null;
  }
}
